// @ts-check

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Divider from '@material-ui/core/Divider';
import AddBoxIcon from '@material-ui/icons/AddBox';
import { blueGrey } from '@material-ui/core/colors';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Accordion from '../../custom/Accordion';
import AccordionSummary from '../../custom/AccordionSummary';
import AccordionDetails from '../../custom/AccordionDetails';
import { v4 as uuidv4 } from 'uuid';
import clsx from 'clsx';
import useFormStyles, { getRandomColor } from '../../common/FormStyle';
import { NewMixerLabel, CustomThumb } from './unitTypesHelpers/MixerLabel';
import MixerSlider from './unitTypesHelpers/MixerSlider';
import { mixBy } from '../common/constants'
import { buildingType } from '../../common/constants'

import {
  SET_MIX_BY,
  ADD_UNIT_TYPE,
  SET_MIXER_VALS,
  SET_FLAT_DEPTH,
  INPUT_CHANGED
} from '../../../redux/actions/actionTypes';
import SimpleTextInput from '../common/SimpleTextInput';
import { FlatUnitTypes } from './FlatUnitTypes';
import { HouseUnitTypes } from './HouseUnitTypes';

const AddUnitTypeButton = withStyles((theme) => ({
  root: {
    marginTop: "5px",
    color: theme.palette.getContrastText(blueGrey["800"]),
    backgroundColor: blueGrey["800"],
    '&:hover': {
      backgroundColor: blueGrey["900"]
    }
  }
}))(Button);

const calcSliderVals = (unitTypes) => {
  const sliderVals = []
  let count = 0
  const unitTypesWithoutLast = unitTypes.slice(0, unitTypes.length - 1)
  for (const ut of unitTypesWithoutLast) {
    count += ut.mixerVal
    sliderVals.push(count)
  }
  return sliderVals
}

function UnitTypes() {
  const formStyles = useFormStyles();
  const dispatch = useDispatch()

  const rootState = useSelector(state => state);
  const buildingInfoState = useSelector(state => state.globalFormData.buildingInfo);
  const validationState = useSelector(state => state.globalFormData.validation);
  const currBuildingType = buildingInfoState.building_type
  const flatTypes = [buildingType.I, buildingType.L, buildingType.O]
  const houseTypes = [buildingType.H, buildingType.D, buildingType.T]
  const sliderVals = calcSliderVals(buildingInfoState.unit_types)

  const unitTypes = buildingInfoState.unit_types // TODO: arik - rename units

  // Update slider and mixer values
  function updateSlider(event, splitterVals) {
    dispatch({
      type: SET_MIXER_VALS,
      payload: splitterVals
    })
  }

  // Snackbar
  const [snackBarOpen, setSnackBar] = useState(false);

  // Event listener for closing snackbar
  function handleClose(event, reason) {
    if (reason === "clickaway") {
      return;
    }
    setSnackBar(false);
  }
  const [isError, setIsError] = useState(false)
  const error_message = "Your units are too deep and can't fit into your plot. Please make them narrower."
  useEffect(() => {

    const plot_length = +rootState.globalFormData.buildingInfo.plot_length
    const plot_width = +rootState.globalFormData.buildingInfo.plot_width

    const min_plotDimention = (plot_length < plot_width) ? plot_length : plot_width;
    const flat_depth = rootState.globalFormData.buildingInfo.flat_depth;



    if (flat_depth <= (min_plotDimention - 2) / 2 && rootState.globalFormData.buildingInfo.building_type == "I") {

      setIsError(false)
    } else {
      setIsError(true)
    }

  }, [+rootState.globalFormData.buildingInfo.plot_length,
  rootState.globalFormData.buildingInfo.plot_width,
  rootState.globalFormData.buildingInfo.flat_depth])


  // Expand/ Collapse
  const units = rootState.globalFormData.buildingInfo.units;
  const [expanded, setExpanded] = useState(false);
  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Accordion expanded={expanded} onChange={handleExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls="unitType-content"
        id="unitType-header"
        IconButtonProps={{
          className: formStyles.expandButton
        }}
      >
        <Typography className={clsx(formStyles.accordionSubheader, formStyles.bold)}>Units</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Box className={formStyles.w100}>
          {flatTypes.includes(currBuildingType) &&
            <>
              <SimpleTextInput label={`Unit depth (${units})`} name="flat_depth" type="number"
                error={!validationState.flat_depth.valid}
                helperText={!validationState.flat_depth.valid &&
                  (units === 'm' ? validationState.flat_depth.errText : validationState.flat_depth.errTextFt)}
                value={buildingInfoState.flat_depth}
                onChange={(event) => {
                  dispatch({
                    type: SET_FLAT_DEPTH,
                    payload: parseFloat(event.target.value)
                  })
                }}
                onBlur={(event) => {
                  dispatch({
                    type: INPUT_CHANGED,
                    payload: {
                      rootState
                    }
                  })
                }}
              />

              <div>
                {isError &&
                  <Typography style={{ color: 'Red' }}>
                    {error_message}
                  </Typography>
                }
              </div>
            </>

          }

          <Typography className={clsx(formStyles.accordionTitle, formStyles.bold)}>
            Define unit types
          </Typography>

          {flatTypes.includes(currBuildingType) &&
            <FlatUnitTypes />
          }

          {houseTypes.includes(currBuildingType) &&
            <HouseUnitTypes />
          }

          <AddUnitTypeButton
            disabled={unitTypes.length > 4}
            variant="contained"
            startIcon={<AddBoxIcon />}
            onClick={(event) => {
              dispatch({
                type: ADD_UNIT_TYPE
              })
            }}
            fullWidth
          >
            Add types
          </AddUnitTypeButton>

          <Divider className={formStyles.divider} />

          <Typography className={clsx(formStyles.accordionTitle, formStyles.bold)}>
            Unit mixer
          </Typography>
          <Typography className={formStyles.bold} variant="subtitle2" color="textSecondary">
            Define the units mix
          </Typography>

          {/* Unit mixer sliders */}
          <Box paddingY={1} >
            <MixerSlider
              track={false}
              value={sliderVals}
              min={0}
              max={100}
              step={1}
              onChange={updateSlider}
              ThumbComponent={CustomThumb}
            />

            {/* Labels */}
            <Box display="flex">
              {unitTypes.map((val, n) => (
                <NewMixerLabel key={n} index={n} id={val.id} mixerValue={val.mixerVal} color={val.color} name={val.name} />
              ))}
            </Box>
          </Box>

          <Divider className={formStyles.divider} />

          <FormControl component="fieldset">
            <Typography className={clsx(formStyles.accordionTitle, formStyles.bold)}>
              Mix by
            </Typography>

            <RadioGroup
              row aria-label="position"
              name="position"
              defaultValue="top"
              onChange={(event) => {
                dispatch({
                  type: SET_MIX_BY,
                  payload: event.target.value
                })
              }}
            >

              <FormControlLabel
                value={mixBy.AREA}
                control={<Radio color="secondary" />}
                label="Area"
                labelPlacement="end"
                checked={buildingInfoState.mix_by === mixBy.AREA}
              />
              <FormControlLabel
                value={mixBy.NUM_OF_UNITS}
                control={<Radio color="secondary" />}
                label="Number of units"
                labelPlacement="end"
                checked={buildingInfoState.mix_by === mixBy.NUM_OF_UNITS}
              />

            </RadioGroup>
          </FormControl>

          {/* Alert */}
          <Snackbar
            open={snackBarOpen}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center"
            }}
            onClose={handleClose}
            message="Atleast one unit is required"
            autoHideDuration={6000}
            action={
              <IconButton color="inherit" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            }
          />
        </Box>

      </AccordionDetails>
    </Accordion>
  )
}

function makeid(length) {
  var result = '';
  var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() *
      charactersLength));
  }
  return `Unit ${result}`;
}


const createNewUnitType = (length, units, unitTypes) => {
  // debugger;
  const mlength = (length)
  let newType = {}

  const types = unitTypes.map(ut => ut.name);
  const areas = unitTypes.map(ut => ut.floor_area);

  // console.log('units', units, 'length', length)
  // console.log(unitTypes);

  if (units === 'm') {
    newType = {
      id: uuidv4(),
      name: ['A', 'B', 'C', 'D', 'E', 'F'].filter((item) => types.includes(item) == false)[0],
      color: getRandomColor(),
      floor_area: [50, 60, 70, 80, 90, 100].filter((item) => areas.includes(item) == false)[0], // initial value
      unit_type: makeid(2),
      storeys: 2,
      house_width: 7,
      cost_sqr_meter: 1000, // initial value
      revenue_sqr_meter: 1000 // initial value
    }
  } else {
    newType = {
      id: uuidv4(),
      name: ['A', 'B', 'C', 'D', 'E', 'F'].filter((item) => types.includes(item) == false)[0],
      color: getRandomColor(),
      floor_area: [500, 600, 700, 800, 900, 1000].filter((item) => areas.includes(item) == false)[0], // initial value
      unit_type: makeid(2),
      storeys: 2,
      house_width: 23,
      cost_sqr_meter: 1000, // initial value
      revenue_sqr_meter: 1000 // initial value
    }
  }

  return newType
}



export const addUnitType = (unitTypes, units) => {
  const newUnitType = createNewUnitType(unitTypes.length, units, unitTypes)
  unitTypes = unitTypes || []
  unitTypes.push(newUnitType)
  const mixerVal = Math.floor(100 / unitTypes.length)
  const remainder = 100 % unitTypes.length
  const updatedUnitTypes = unitTypes.map(ut => {
    return {
      ...ut,
      mixerVal
    }
  })

  if (remainder > 0) {
    updatedUnitTypes[updatedUnitTypes.length - 1].mixerVal++ // complete to 100
  }

  return updatedUnitTypes
}

export const deleteUnitType = (unitTypes, unitIdToDelete) => {
  const filteredUnitTypes = unitTypes.filter(ut => ut.id !== unitIdToDelete)
  const mixerVal = Math.floor(100 / filteredUnitTypes.length)
  const remainder = 100 % filteredUnitTypes.length
  const updatedUnitTypes = filteredUnitTypes.map(ut => {
    return {
      ...ut,
      mixerVal
    }
  })
  if (remainder > 0) {
    updatedUnitTypes[updatedUnitTypes.length - 1].mixerVal++ // complete to 100
  }

  return updatedUnitTypes
}

export default UnitTypes;